import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp } from 'lucide-react';

const RoasCalculatorTool = () => {
  const [revenue, setRevenue] = useState('');
  const [adSpend, setAdSpend] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const rev = parseFloat(revenue);
    const spend = parseFloat(adSpend);

    if (isNaN(rev) || isNaN(spend) || rev < 0 || spend <= 0) {
      setResult(null);
      return;
    }

    // ROAS = Revenue / Ad Spend
    const roas = rev / spend;
    const roasPercent = roas * 100;
    const profit = rev - spend;
    const isProfitable = roas > 1;

    setResult({
      revenue: rev,
      adSpend: spend,
      roas,
      roasPercent,
      profit,
      isProfitable
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setRevenue('');
    setAdSpend('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Revenue ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="revenue-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ad Spend ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={adSpend}
              onChange={(e) => setAdSpend(e.target.value)}
              placeholder="e.g., 2500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="ad-spend-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-roas-btn"
      >
        <Calculator size={18} />
        Calculate ROAS
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="roas-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className={result.isProfitable ? 'text-green-400' : 'text-red-400'} />
                <span className="text-sm text-gray-400">Return on Ad Spend</span>
              </div>
              <button
                onClick={() => copyResult(result.roas)}
                className={`p-1.5 rounded transition-colors ${
                  copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-roas"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className={`text-4xl font-bold ${result.isProfitable ? 'text-green-400' : 'text-red-400'}`} data-testid="roas-result">
              {result.roas.toFixed(2)}x
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {result.roasPercent.toFixed(0)}% return
            </div>
          </div>

          {/* Profit/Loss Indicator */}
          <div className={`p-4 rounded-lg ${result.isProfitable ? 'bg-green-900/20 border border-green-800/50' : 'bg-red-900/20 border border-red-800/50'}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">
                {result.isProfitable ? 'Profitable Campaign' : 'Unprofitable Campaign'}
              </span>
              <span className={`font-bold ${result.isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                {result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {result.isProfitable 
                ? `For every $1 spent, you earned ${formatCurrency(result.roas)} in revenue.`
                : `You're losing money. ROAS needs to be above 1.0x to break even.`
              }
            </p>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Revenue</span>
                <span className="text-white font-medium">{formatCurrency(result.revenue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Ad Spend</span>
                <span className="text-white font-medium">{formatCurrency(result.adSpend)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Gross Profit</span>
                <span className={`font-medium ${result.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">ROAS</span>
                <span className={`font-medium ${result.isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                  {result.roas.toFixed(2)}x
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">ROAS Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          ROAS = Revenue / Ad Spend
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">ROAS Benchmarks</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Break-even</div>
            <div>1.0x</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Good</div>
            <div>3.0x - 4.0x</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Excellent</div>
            <div>5.0x+</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips to Improve ROAS</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Focus on high-intent keywords and audiences</div>
          <div>• Improve ad relevance and quality scores</div>
          <div>• Optimize landing pages for conversion</div>
          <div>• Test different creatives and messaging</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-roas-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default RoasCalculatorTool;
